export type BitmapFontTextMetrics = {
  width: number
  height: number
  ascent: number
  descent: number
}

let bitmapFontMeasureCanvas: HTMLCanvasElement | null = null

const GENERIC_FONT_FAMILIES = new Set([
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
])

function getBitmapFontMeasureContext(): CanvasRenderingContext2D {
  if (!bitmapFontMeasureCanvas) {
    bitmapFontMeasureCanvas = document.createElement('canvas')
  }
  const ctx = bitmapFontMeasureCanvas.getContext('2d')
  if (!ctx) {
    throw new Error('bitmap font measure context create failed')
  }
  return ctx
}

export function measureBitmapFontText(
  text: string,
  fontSize: number,
  fontFamily: string,
): BitmapFontTextMetrics {
  const ctx = getBitmapFontMeasureContext()
  return measureBitmapFontTextWithContext(ctx, text, fontSize, fontFamily)
}

export type ChengguangBitmapGlyphMetrics = {
  width: number
  height: number
  aboveLine: number
  underlineSize: number
}

/**
 * 3.0 `getTextSize`：用扣过 padding 的字号量，宽 round，高取墨迹与字号较大值。
 */
export function measureChengguangBitmapGlyph(
  text: string,
  fontFamily: string,
  rasterFontSize: number,
): ChengguangBitmapGlyphMetrics {
  const raster = Math.max(2, Math.round(rasterFontSize || 0))
  const ctx = getBitmapFontMeasureContext()
  ctx.textBaseline = 'alphabetic'
  ctx.font = `${raster}px ${formatCanvasFontFamily(fontFamily)}`
  const metrics = ctx.measureText(text || '')
  const width = Math.round(metrics.width || raster)
  const actualAscent = Math.ceil(metrics.actualBoundingBoxAscent || 0)
  const actualDescent = Math.ceil(metrics.actualBoundingBoxDescent || 0)
  const fontAscent = Math.ceil(metrics.fontBoundingBoxAscent || 0)
  const fontDescent = Math.ceil(metrics.fontBoundingBoxDescent || 0)
  return {
    width: Math.max(1, width),
    height: Math.max(raster, actualAscent + actualDescent, fontAscent + fontDescent),
    aboveLine: fontAscent,
    underlineSize: actualDescent,
  }
}

export function measureBitmapFontTextWithContext(
  ctx: CanvasRenderingContext2D,
  text: string,
  fontSize: number,
  fontFamily: string,
): BitmapFontTextMetrics {
  const normalizedFontSize = Math.max(1, Math.round(fontSize || 0))
  ctx.textBaseline = 'alphabetic'
  ctx.font = `${normalizedFontSize}px ${formatCanvasFontFamily(fontFamily)}`
  const metrics = ctx.measureText(text || 'Ag')
  const width = Math.max(1, Math.ceil(metrics.width || normalizedFontSize))
  const ascent = Math.max(
    1,
    Math.ceil(metrics.actualBoundingBoxAscent || Math.round(normalizedFontSize * 0.78)),
  )
  const descent = Math.max(
    0,
    Math.ceil(metrics.actualBoundingBoxDescent || Math.round(normalizedFontSize * 0.22)),
  )
  const height = Math.max(
    1,
    Math.ceil(
      Math.max(
        normalizedFontSize,
        (metrics.actualBoundingBoxAscent || 0) + (metrics.actualBoundingBoxDescent || 0),
      ),
    ),
  )
  return {
    width,
    height,
    ascent,
    descent,
  }
}

export function formatCanvasFontFamily(fontFamily: string): string {
  const cleanFamily = String(fontFamily || '').trim()
  if (!cleanFamily) return 'sans-serif'
  if (cleanFamily.includes(',') || cleanFamily.includes('"') || cleanFamily.includes("'")) {
    return cleanFamily
  }
  if (GENERIC_FONT_FAMILIES.has(cleanFamily.toLowerCase())) {
    return cleanFamily
  }
  return `"${cleanFamily.replace(/"/g, '\\"')}"`
}
